import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, paperClasses } from '@mui/material';
import Typography from '@mui/material/Typography';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSetAtom } from 'jotai';
import { Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { convertProductToInsertInput } from '@kheops/utils';
import { commonSnackbarPropsAtom } from '../../common/state/state';
import { useOptimizeProductNameMutation } from '../../mutations/__generated__/optimizeProductName.generated';
import { ProductNamesToOptimizeCountDocument } from '../../queries/__generated__/productNamesToOptimizeCount.generated';
import { ProductsByCompanyDocument } from '../../queries/__generated__/productsByCompany.generated';
import { ProductVersionByIdDocument } from '../../queries/__generated__/productVersionById.generated';
import { ProductFragment } from '../products';
import NamingWizard from './NamingWizard';
import NamingSuggestionField from './NamingSuggestionField';
import useProductNameSuggestionWizard from './useProductNameSuggestionWizard';

interface ProductNameOptimizationDialogProps {
  product: ProductFragment;
  onClose: () => void;
  // when set, the success snackbar offers to open the product page
  productLink?: string;
  onApply?: (name: string) => void;
}

export default function ProductNameOptimizationDialog({ product, onClose, productLink, onApply }: ProductNameOptimizationDialogProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'products']);
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const [optimizeProductName] = useOptimizeProductNameMutation({
    refetchQueries: [ProductVersionByIdDocument, ProductsByCompanyDocument, ProductNamesToOptimizeCountDocument],
  });
  const [editedName, setEditedName] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);

  const handleComplete = async (finalName: string): Promise<void> => {
    if (onApply) {
      onApply(finalName);
      setCommonSnackbarProps({
        label: t('products:name_optimization.success'),
        snackbarProps: { open: true },
      });
      onClose();

      return;
    }

    setIsSaving(true);

    try {
      await optimizeProductName({
        variables: {
          product: { ...convertProductToInsertInput(product), name: finalName },
          companyId: product.company_id,
          sku: product.sku,
        },
      });
      setCommonSnackbarProps({
        label: t('products:name_optimization.success'),
        snackbarProps: { open: true },
        alertProps: productLink
          ? {
            action: (
              <Button color="success" component={Link} to={productLink}>
                {t('products:name_optimization.see_product')}
              </Button>
            ),
          }
          : undefined,
      });
    } catch {
      setCommonSnackbarProps({
        label: t('products:name_optimization.failed'),
        snackbarProps: { open: true },
        alertProps: { severity: 'error' },
      });
    }

    onClose();
  };

  const handleError = (): void => {
    setCommonSnackbarProps({
      label: t('products:naming_wizard.error'),
      snackbarProps: { open: true },
      alertProps: { severity: 'error' },
    });
    onClose();
  };

  const wizard = useProductNameSuggestionWizard({
    name: product.name,
    subFamily: product.sub_family as Product_Sub_Family_Name_Enum,
    brand: product.brand,
    description: product.description,
    onComplete: handleComplete,
    onError: handleError,
  });
  const isFinal = wizard.status === 'final' || isSaving;
  const finalName = editedName ?? wizard.suggestedName;

  useEffect(() => {
    wizard.start();
  }, []);

  const handleCancel = (): void => {
    if (isSaving) {
      return;
    }

    setCommonSnackbarProps({
      label: t('products:name_optimization.cancelled'),
      snackbarProps: { open: true },
      alertProps: { severity: 'info' },
    });
    onClose();
  };

  const handleBack = (): void => {
    setEditedName(undefined);
    wizard.back();
  };

  return (
    <Dialog
      open
      fullWidth
      onClose={handleCancel}
      sx={{
        [`& .${paperClasses.root}`]: {
          maxWidth: 640,
        },
      }}
    >
      <DialogTitle sx={{ flexDirection: 'row', alignItems: 'center', gap: 1.5 }}>
        <AutoAwesomeOutlinedIcon />
        {t('products:name_optimization.dialog_title')}
        <IconButton aria-label={t('common:close')} onClick={handleCancel} sx={{ ml: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 4, pb: isFinal ? 0 : 3 }}>
        {isFinal ? (
          <NamingSuggestionField suggestedName={wizard.suggestedName} value={finalName} onChange={setEditedName} />
        ) : (
          <>
            <Typography variant="bodyMedium" color="onSurfaceVariant">
              {t('products:name_optimization.dialog_description')}
            </Typography>
            {wizard.status !== 'idle' && <NamingWizard {...wizard} onBack={wizard.canGoBack ? handleBack : undefined} />}
          </>
        )}
      </DialogContent>
      {isFinal && (
        <DialogActions>
          <Box sx={{ display: 'flex', width: { xs: '100%', sm: 'auto' }, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' }, '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' } } }}>
            <Button variant="outlined" disabled={isSaving} onClick={handleCancel}>
              {t('common:cancel')}
            </Button>
            {wizard.canGoBack && (
              <Button variant="outlined" onClick={handleBack} sx={{ mr: 'auto' }}>
                {t('common:previous')}
              </Button>
            )}
            <Button variant="contained" loading={isSaving} disabled={!finalName.trim()} onClick={() => wizard.acceptSuggestion(finalName)}>
              {t('products:name_optimization.validate')}
            </Button>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
}
